import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { COMMUNITIES, EVENTS, FERTILITY_ARTICLES, FERTILITY_PRODUCTS } from '../data';
import { useNovaTriggerCheck } from '../hooks/useNovaTriggerCheck';
import { useMediaQuery } from '../hooks/useMediaQuery';
import DashboardAppShell from '../components/DashboardAppShell';
import NovaSidebarWithCollapse from '../components/NovaSidebarWithCollapse';
import NovaAvatarImg from '../components/NovaAvatarImg';
import { normalizeDemoChapterKey } from '../data/demoChapterNova';
import { layoutMobileMediaQuery } from '../designSystem';
import { FERTILITY_DEMO_STATUS_PILL_TEXT } from '../data/pageHeaderConfig';

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";

const CHAPTER_CONFIG = {
  ttc: { label: 'Trying to Conceive', color: '#7F77DD', bg: '#EEEDFE' },
  fertility: { label: 'Fertility', color: '#D4537E', bg: '#FBEAF0' },
  pregnancy: { label: 'Pregnancy', color: '#14B8A6', bg: '#F0FDFA' },
  postpartum: { label: 'Motherhood', color: '#9B2942', bg: '#F5EAED' },
  ivf: { label: 'Fertility', color: '#D4537E', bg: '#FBEAF0' },
};

const LEFT_NAV = {
  myJourney: [
    { path: '/', label: 'Home' },
    { path: '/health', label: 'Health record' },
    { path: '/doctors', label: 'My doctors' },
    { path: '/medications', label: 'Medications' },
  ],
  connect: [
    { path: '/community', label: 'Community' },
    { path: '/connect', label: 'Connect' },
    { path: '/events', label: 'Events near me' },
  ],
  discover: [
    { path: '/resources', label: 'Resources' },
    { path: '/products', label: 'Products' },
  ],
};

const MOCK_WOMEN = [
  { initials: 'JM', name: 'Jasmine M.', detail: 'Fertility · Cycle 2 · Columbus, OH' },
  { initials: 'NR', name: 'Noelle R.', detail: 'IVF stims · Day 8 · Cleveland' },
  { initials: 'AK', name: 'Ava K.', detail: 'TTC · 4 months · Cincinnati' },
];

const EVENT_COLORS = {
  nova: '#D4537E',
  shared: '#7F77DD',
  health: '#1D9E75',
  connection: '#185FA5',
  milestone: '#E8602C',
};

function getStatsForChapter(chapter, timeline, logs) {
  const ch = (chapter || 'fertility').toLowerCase();
  const due = timeline?.key_dates?.due_date;
  const lastPeriod = timeline?.cycle_data?.last_period_date;
  const child = timeline?.children?.[0];
  const protocol = timeline?.ivf_protocol;

  if (ch === 'fertility' || ch === 'ivf') {
    return [
      { label: 'IVF cycles', value: protocol?.cycle_number ?? 1 },
      { label: 'Stim days', value: protocol?.cycle_day ?? 8 },
      { label: 'Follicles (last scan)', value: protocol?.follicles ?? 12 },
    ];
  }
  if (ch === 'ttc') {
    const daysSince = lastPeriod ? Math.floor((Date.now() - new Date(lastPeriod)) / 86400000) : 14;
    return [
      { label: 'Cycle day', value: daysSince },
      { label: 'Days trying', value: '4 mo' },
      { label: 'TWW countdown', value: '—' },
    ];
  }
  if (ch === 'pregnancy') {
    const daysToDue = due ? Math.ceil((new Date(due) - Date.now()) / 86400000) : 140;
    const weeks = due ? Math.max(1, Math.min(40, Math.floor(40 - daysToDue / 7))) : 20;
    return [
      { label: 'Weeks pregnant', value: weeks },
      { label: 'Days to due date', value: daysToDue },
      { label: 'Trimester', value: weeks < 14 ? 1 : weeks < 28 ? 2 : 3 },
    ];
  }
  if (ch === 'postpartum') {
    const birthDate = child?.dob || timeline?.key_dates?.birth_date;
    const daysSince = birthDate ? Math.floor((Date.now() - new Date(birthDate)) / 86400000) : 42;
    return [
      { label: 'Days since birth', value: daysSince },
      { label: 'Baby age', value: daysSince < 30 ? `${daysSince}d` : `${Math.floor(daysSince / 30)} mo` },
      { label: 'Mood average', value: '—' },
    ];
  }
  if (ch === 'menopause') {
    const symptomLogs = (logs || []).filter((l) => l.log_type === 'hot_flash' || l.log_type === 'symptom');
    return [
      { label: 'Days tracked', value: 14 },
      { label: 'Symptoms logged', value: symptomLogs.length || 3 },
      { label: 'HRT days', value: '—' },
    ];
  }
  // first-chapter / cycle
  const daysSincePeriod = lastPeriod ? Math.floor((Date.now() - new Date(lastPeriod)) / 86400000) : 14;
  return [
    { label: 'Days since last period', value: daysSincePeriod },
    { label: 'Cycle length', value: timeline?.cycle_data?.avg_cycle_length ?? 28 },
    { label: 'Mood streak', value: '3 days' },
  ];
}

function getHealthSnapshot(chapter, timeline, user) {
  const ch = (chapter || 'fertility').toLowerCase();
  if (ch === 'fertility' || ch === 'ivf') {
    return [
      ['Protocol', timeline?.ivf_protocol?.protocol_type ?? '—'],
      ['AMH', '—'],
      ['Clinic', timeline?.ivf_protocol?.clinic_name ?? '—'],
      ['Doctor', 'Dr. Jain ★ 4.9'],
      ['Last cycle', timeline?.key_dates?.retrieval_date ? new Date(timeline.key_dates.retrieval_date).toLocaleDateString() : '—'],
      ['Medications', 'Gonal-F, Menopur'],
    ];
  }
  if (ch === 'ttc') {
    return [
      ['Cycle length', `${timeline?.cycle_data?.avg_cycle_length ?? 28} days`],
      ['Last period', timeline?.cycle_data?.last_period_date ?? '—'],
      ['Tracking method', 'App + LH tests'],
      ['Months trying', '4'],
    ];
  }
  if (ch === 'pregnancy') {
    return [
      ['Due date', timeline?.key_dates?.due_date ?? '—'],
      ['Provider', '—'],
      ['Birth preference', '—'],
      ['Weeks along', '—'],
    ];
  }
  if (ch === 'postpartum') {
    const child = timeline?.children?.[0];
    return [
      ['Birth date', child?.dob ?? '—'],
      ['Birth type', '—'],
      ['Breastfeeding', '—'],
      ['Pediatrician', '—'],
    ];
  }
  if (ch === 'menopause') {
    return [
      ['Stage', 'Perimenopause'],
      ['Last period', '—'],
      ['Symptoms', 'Hot flashes, sleep'],
      ['HRT status', '—'],
    ];
  }
  return [
    ['Cycle length', `${timeline?.cycle_data?.avg_cycle_length ?? 28} days`],
    ['Last period', timeline?.cycle_data?.last_period_date ?? '—'],
  ];
}

// ── Resources (curated from credible sources across the internet) ─
function ResourcesArticles({ articles, navigate, fontSerif, fontBody }) {
  const sorted = [...(articles || [])].sort((a, b) => (b.evidenceBased ? 1 : 0) - (a.evidenceBased ? 1 : 0));
  return (
    <div style={{ fontFamily: fontBody }}>
      <h1 style={{ fontFamily: fontSerif, fontSize: 28, color: 'var(--text)', marginBottom: 6 }}>Resources</h1>
      <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 28 }}>Curated from credible sources across the internet — NIH, Mayo Clinic, Cleveland Clinic, Resolve, major publishers, and peer-reviewed journals. Evidence-based first, plus real stories from women who have been there.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {sorted.map((a) => (
          <article
            key={a.id}
            onClick={() => navigate('/signup')}
            style={{
              display: 'grid',
              gridTemplateColumns: '200px 1fr',
              gap: 24,
              alignItems: 'stretch',
              background: 'var(--card)',
              border: '1px solid var(--border)',
              borderRadius: 14,
              overflow: 'hidden',
              cursor: 'pointer',
              transition: 'border-color 0.2s, box-shadow 0.2s',
            }}
            onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--pink-m)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.06)'; }}
            onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={{ width: 200, minHeight: 120, flexShrink: 0, overflow: 'hidden' }}>
              <img src={a.img} alt="" style={{ width: '100%', height: '100%', minHeight: 120, objectFit: 'cover' }} />
            </div>
            <div style={{ padding: '20px 24px 20px 0', minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{a.source} · {a.date}</span>
                {a.evidenceBased && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: 'var(--sage-l, #E1F5EE)', color: 'var(--sage-d, #0F6E56)' }}>Evidence-based</span>
                )}
              </div>
              <h2 style={{ fontFamily: fontSerif, fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1.35, marginBottom: 8 }}>{a.title}</h2>
              <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.5, marginBottom: 12 }}>{a.excerpt}</p>
              <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 999, background: 'var(--pink-l)', color: 'var(--pink-d)', alignSelf: 'flex-start' }}>{a.category}</span>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

// ── Products (top-rated fertility products) ────────────────────
function ProductsGrid({ products, navigate, fontSerif, fontBody }) {
  return (
    <div style={{ fontFamily: fontBody }}>
      <h1 style={{ fontFamily: fontSerif, fontSize: 28, color: 'var(--text)', marginBottom: 6 }}>Products</h1>
      <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 28 }}>Top-rated fertility products — ovulation tests, supplements, and trackers.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
        {products.map((p) => (
          <article
            key={p.id}
            onClick={() => navigate('/signup')}
            style={{
              background: 'var(--card)',
              border: '1px solid var(--border)',
              borderRadius: 14,
              overflow: 'hidden',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              transition: 'border-color 0.2s, box-shadow 0.2s',
            }}
            onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--pink-m)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.06)'; }}
            onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={{ position: 'relative', height: 160, overflow: 'hidden' }}>
              <img src={p.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <div style={{ position: 'absolute', top: 12, left: 12, display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.95)', padding: '6px 10px', borderRadius: 999, fontSize: 13, fontWeight: 800, color: 'var(--text)' }}>
                <span style={{ color: '#EAB308' }}>★</span> {p.rating} <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--muted)' }}>({p.reviews >= 1000 ? `${(p.reviews / 1000).toFixed(1)}k` : p.reviews})</span>
              </div>
              <div style={{ position: 'absolute', bottom: 12, right: 12, background: 'var(--pink)', color: 'white', fontSize: 12, fontWeight: 800, padding: '6px 12px', borderRadius: 999 }}>{p.digs} digs</div>
            </div>
            <div style={{ padding: 18, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>{p.brand}</div>
              <h2 style={{ fontFamily: fontSerif, fontSize: 16, fontWeight: 700, color: 'var(--text)', lineHeight: 1.35, marginBottom: 6 }}>{p.name}</h2>
              <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5, flex: 1 }}>{p.excerpt}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                <span style={{ fontSize: 18, fontWeight: 900, color: 'var(--text)' }}>${p.price}</span>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 999, background: 'var(--pink-l)', color: 'var(--pink-d)' }}>{p.category}</span>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function buildTimelineEvents(logs, firedTriggers, socialPosts, userId) {
  const events = [];
  const key = String(userId || '').toLowerCase();
  (logs || []).forEach((l) => {
    if (String(l.user_id || '').toLowerCase() !== key) return;
    events.push({
      type: 'health',
      title: `${l.log_type} logged`,
      at: l.logged_at,
      color: EVENT_COLORS.health,
    });
  });
  (firedTriggers || []).forEach((t) => {
    if (String(t.user_id || '').toLowerCase() !== key) return;
    events.push({
      type: 'nova',
      title: 'Nova reached out',
      at: t.fired_at,
      color: EVENT_COLORS.nova,
      bubble: 'Checked in on your journey',
    });
  });
  (socialPosts || []).filter((p) => String(p?.userEmail || '').toLowerCase() === key).forEach((p) => {
    events.push({
      type: 'shared',
      title: 'You shared',
      at: p.createdAt,
      color: EVENT_COLORS.shared,
      bubble: p.content?.slice(0, 80),
      reactions: { hearts: 12, sameBoat: 3, replies: 2 },
    });
  });
  events.sort((a, b) => new Date(b.at) - new Date(a.at));
  return events.slice(0, 5);
}

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    journey,
    getJourneyTimeline,
    getLogs,
    getLossHistory,
    firedTriggers,
    addFiredTrigger,
    addNovaNotification,
    getNovaNotificationsForUser,
    markNovaNotificationRead,
    socialPosts,
    getDiscoverProfile,
    setNovaDraft,
  } = useApp();

  const timeline = getJourneyTimeline(user);
  const logs = getLogs?.() ?? [];
  const lossHistory = getLossHistory?.(user) ?? [];
  const chapter = (user?.journeyType || timeline?.chapter || 'fertility').toLowerCase();
  const chapterKey = chapter === 'ivf' ? 'fertility' : chapter === 'cycle' ? 'first-chapter' : chapter;
  const chapterInfo = CHAPTER_CONFIG[chapterKey] || CHAPTER_CONFIG.fertility;
  const novaAvatarChapter = normalizeDemoChapterKey(chapter) === 'ttc' ? 'ttc' : 'fertility';

  useNovaTriggerCheck({
    user,
    timeline,
    logs,
    firedTriggers,
    lossHistory,
    addFiredTrigger,
    addNovaNotification,
  });

  const novaNotifications = getNovaNotificationsForUser?.(user) ?? [];
  const latestNova = novaNotifications[0];
  const first_name = user?.first_name || user?.name?.split(/\s+/)[0] || 'there';
  const chapterSubtitle = {
    fertility: FERTILITY_DEMO_STATUS_PILL_TEXT,
    ivf: FERTILITY_DEMO_STATUS_PILL_TEXT,
    ttc: 'Cycle day 14 · Fertile window',
    pregnancy: 'Week 20 · Second trimester',
    postpartum: '6 weeks postpartum',
    menopause: 'Tracking symptoms',
    'first-chapter': 'Learning about your body',
    cycle: 'Cycle day 14',
  }[chapter] || 'Your journey';

  const stats = getStatsForChapter(chapter, timeline, logs);
  const healthRows = getHealthSnapshot(chapter, timeline, user);
  const timelineEvents = buildTimelineEvents(logs, firedTriggers, socialPosts, user?.email);
  const activeRooms = COMMUNITIES.slice(0, 3);

  const novaMessage = latestNova?.message || "I'm here whenever you need me. How are you feeling today? I noticed you're in the middle of your cycle — want to talk through anything?";
  const suggestionChips = ['How am I doing?', 'What should I ask my doctor?', 'Connect me with others'];

  const PLACEHOLDER_TITLES = { '/health': 'Health record', '/medications': 'Medications' };
  const placeholderTitle = PLACEHOLDER_TITLES[location.pathname];
  const isEventsPage = location.pathname === '/events';
  const isResourcesPage = location.pathname === '/resources';
  const isProductsPage = location.pathname === '/products';

  const m = useMediaQuery(layoutMobileMediaQuery);

  return (
    <DashboardAppShell
      fontFamily={FONT_BODY}
      rightColumn={<NovaSidebarWithCollapse navigate={navigate} setNovaDraft={setNovaDraft} />}
    >
        {isEventsPage ? (
          <div>
            <h1 style={{ fontFamily: FONT_SERIF, fontSize: 24, color: 'var(--text)', marginBottom: 8 }}>Events near me</h1>
            <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 24 }}>Fertility, TTC, pregnancy & women's health events in Ohio</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {EVENTS.map((e) => (
                <div
                  key={e.id}
                  style={{
                    background: 'var(--card)',
                    border: '1px solid var(--border)',
                    borderRadius: 12,
                    padding: 18,
                    display: 'flex',
                    gap: 16,
                    alignItems: 'flex-start',
                  }}
                >
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: chapterInfo.bg, color: chapterInfo.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                    {e.type === 'virtual' ? '💻' : '📍'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>{e.title}</div>
                    <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 6 }}>{e.desc}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {e.date} · {e.time} · {e.location}
                      <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 999, background: chapterInfo.bg, color: chapterInfo.color, fontWeight: 600, fontSize: 11 }}>{e.type}</span>
                    </div>
                  </div>
                  <button type="button" onClick={() => navigate('/signup')} style={{ fontSize: 12, fontWeight: 600, color: 'white', background: 'var(--pink)', border: 'none', padding: '8px 16px', borderRadius: 999, cursor: 'pointer', flexShrink: 0 }}>Join</button>
                </div>
              ))}
            </div>
          </div>
        ) : isResourcesPage ? (
          <ResourcesArticles articles={FERTILITY_ARTICLES} navigate={navigate} fontSerif={FONT_SERIF} fontBody={FONT_BODY} />
        ) : isProductsPage ? (
          <ProductsGrid products={FERTILITY_PRODUCTS} navigate={navigate} fontSerif={FONT_SERIF} fontBody={FONT_BODY} />
        ) : placeholderTitle ? (
          <div>
            <h1 style={{ fontFamily: FONT_SERIF, fontSize: 24, color: 'var(--text)', marginBottom: 12 }}>{placeholderTitle}</h1>
            <p style={{ fontSize: 14, color: 'var(--muted)' }}>This page is coming soon.</p>
          </div>
        ) : (
        <>
        {/* Top bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h1 style={{ fontFamily: FONT_SERIF, fontSize: 24, color: 'var(--text)', marginBottom: 4 }}>
              Good morning, <em style={{ fontStyle: 'italic', color: 'var(--pink)' }}>{first_name}</em>
            </h1>
            <p style={{ fontSize: 13, color: 'var(--muted)' }}>
              {chapterSubtitle}
              {user?.email && (
                <button type="button" onClick={() => navigate(`/profile/${encodeURIComponent(user.email)}`)} style={{ marginLeft: 12, fontSize: 12, fontWeight: 600, color: 'var(--pink)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                  View & edit your profile →
                </button>
              )}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '6px 12px',
                borderRadius: 999,
                background: chapterInfo.bg,
                color: chapterInfo.color,
              }}
            >
              {chapterInfo.label}
            </span>
            <button
              type="button"
              onClick={() => navigate('/journey')}
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: 'white',
                background: 'var(--pink)',
                border: 'none',
                padding: '8px 16px',
                borderRadius: 999,
                cursor: 'pointer',
              }}
            >
              My Journey →
            </button>
          </div>
        </div>

        {/* ORA CARD */}
        <div
          style={{
            background: '#FBEAF0',
            borderRadius: 12,
            padding: 20,
            marginBottom: 24,
          }}
        >
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 14 }}>
            <div style={{ position: 'relative' }}>
              <NovaAvatarImg size={44} alt="" chapter={novaAvatarChapter} />
              <span
                style={{
                  position: 'absolute',
                  bottom: -2,
                  right: -2,
                  fontSize: 9,
                  fontWeight: 700,
                  padding: '2px 6px',
                  borderRadius: 999,
                  background: 'var(--pink)',
                  color: 'white',
                }}
              >
                live
              </span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--pink-d)', marginBottom: 6 }}>Nova</div>
              <p style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.5, marginBottom: 12 }}>{novaMessage}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                {suggestionChips.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => navigate('/nova')}
                    style={{
                      fontSize: 12,
                      padding: '6px 12px',
                      borderRadius: 999,
                      border: '1px solid var(--pink-m)',
                      background: 'white',
                      color: 'var(--pink-d)',
                      cursor: 'pointer',
                      fontFamily: FONT_BODY,
                      fontWeight: 500,
                    }}
                  >
                    {chip}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => {
                  if (latestNova) markNovaNotificationRead?.(latestNova.id);
                  navigate('/nova');
                }}
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: 'white',
                  background: 'var(--pink)',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: 999,
                  cursor: 'pointer',
                }}
              >
                Open Full Chat With Nova
              </button>
            </div>
          </div>
        </div>

        {/* STATS ROW */}
        <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
          {stats.map((s) => (
            <div
              key={s.label}
              style={{
                background: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: 12,
                padding: 16,
              }}
            >
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>{s.label}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* SHARE BOX */}
        <div
          style={{
            background: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: 12,
            padding: 16,
            marginBottom: 24,
          }}
        >
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
            {(user && getDiscoverProfile?.(user)?.photos?.[0]) ? (
              <img
                src={getDiscoverProfile(user).photos[0]}
                alt=""
                style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
              />
            ) : !user ? (
              <img
                src="/images/chapters/fertility.png"
                alt="Maya"
                style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
              />
            ) : (
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--pink-m)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'var(--pink-d)', flexShrink: 0 }}>
                {user?.name?.[0]?.toUpperCase() || '?'}
              </div>
            )}
            <input
              type="text"
              placeholder="Share something with your chapter..."
              readOnly
              onClick={() => navigate('/community')}
              style={{
                flex: 1,
                padding: '10px 14px',
                border: '1px solid var(--border)',
                borderRadius: 10,
                fontSize: 14,
                color: 'var(--text)',
                background: 'var(--sand)',
                cursor: 'pointer',
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" style={{ fontSize: 12, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--sand)', cursor: 'pointer' }}>📷 Photo</button>
            <button type="button" style={{ fontSize: 12, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--sand)', cursor: 'pointer' }}>🎬 Video</button>
            <button type="button" style={{ fontSize: 12, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--sand)', cursor: 'pointer' }}>GIF</button>
            <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 999, background: chapterInfo.bg, color: chapterInfo.color, marginLeft: 'auto' }}>{chapterInfo.label}</span>
            <button type="button" onClick={() => navigate('/community')} style={{ fontSize: 12, fontWeight: 700, color: 'white', background: 'var(--pink)', border: 'none', padding: '8px 16px', borderRadius: 999, cursor: 'pointer' }}>Post</button>
          </div>
        </div>

        {/* JOURNEY TIMELINE */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontFamily: FONT_SERIF, fontSize: 18, color: 'var(--text)', marginBottom: 16 }}>Your journey this week</h3>
          <div style={{ position: 'relative', paddingLeft: 24 }}>
            <div style={{ position: 'absolute', left: 7, top: 0, bottom: 0, width: 2, background: 'var(--border)' }} />
            {timelineEvents.length > 0 ? (
              timelineEvents.map((ev, i) => (
                <div key={i} style={{ position: 'relative', marginBottom: 20 }}>
                  <div style={{ position: 'absolute', left: -24, top: 4, width: 14, height: 14, borderRadius: '50%', background: ev.color, border: '2px solid white', boxShadow: '0 0 0 1px var(--border)' }} />
                  <div style={{ marginLeft: 4 }}>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>{ev.type === 'nova' ? 'Nova reached out' : ev.type === 'shared' ? 'You shared' : 'Health logged'}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{ev.title}</div>
                    {ev.bubble && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, padding: 8, background: 'var(--sand)', borderRadius: 8 }}>{ev.bubble}</div>}
                    {ev.reactions && (
                      <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 11, color: 'var(--muted)' }}>
                        <span>❤️ {ev.reactions.hearts}</span>
                        <span>Same boat {ev.reactions.sameBoat}</span>
                        <span>{ev.reactions.replies} replies</span>
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{new Date(ev.at).toLocaleDateString()}</div>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ padding: 24, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, color: 'var(--muted)', fontSize: 14 }}>No recent activity. Start by logging your journey or sharing with your chapter.</div>
            )}
          </div>
        </div>

        {/* ORA CHAT BOX */}
        <div>
          <h3 style={{ fontFamily: FONT_SERIF, fontSize: 18, color: 'var(--text)', marginBottom: 12 }}>Ask Nova</h3>
          <div
            style={{
              border: '2px solid var(--pink-m)',
              borderRadius: 10,
              padding: 16,
              background: 'var(--white)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ position: 'relative' }}>
                <NovaAvatarImg size={36} alt="" chapter={novaAvatarChapter} />
                <div style={{ position: 'absolute', bottom: 0, right: 0, width: 8, height: 8, borderRadius: '50%', background: 'var(--sage)', border: '2px solid white' }} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Nova</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>No question is too small</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                placeholder="Ask Nova anything..."
                onClick={() => navigate('/nova')}
                style={{ flex: 1, padding: '12px 16px', border: '1px solid var(--border)', borderRadius: 10, fontSize: 14 }}
              />
              <button
                type="button"
                onClick={() => navigate('/nova')}
                style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--pink)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 18 }}
              >
                →
              </button>
            </div>
          </div>
        </div>
        </>
        )}
    </DashboardAppShell>
  );
}
